var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Operation as LoggingOperation, Log, LogCategory } from '@augloop/logging';
import { SessionInitMessage, currentProtocolVersion, TokenProvisionMessage, ServerAuthenticationState, TokenFailureMessage } from '@augloop/session-protocol';
import { AuthTokenType } from '@augloop/interfaces';
import { Utils } from '../utils';
import { AuthTokenType as ClientAuthTokenType, AuthTokenResponseError } from '@augloop/schemas';
import { TokenType } from './token-refresh-manager';
import { EventEmitter } from 'eventemitter3';
import { scrubEuiiInUrl } from '../telemetry-logger';
export class SessionInitializer extends EventEmitter {
    constructor(getSessionStats, clientMetadata, tokenRefreshManager, sendMessage, options) {
        super();
        this.getSessionStats = getSessionStats;
        this.clientMetadata = clientMetadata;
        this.tokenRefreshManager = tokenRefreshManager;
        this.sendMessage = sendMessage;
        this.options = options ? options : {};
        this.options.overrideSessionInitMessage = this.options.overrideSessionInitMessage || ((sessionInitMessage) => sessionInitMessage);
        // Best effort setting user system timezone if one is not provided by the host
        if (this.clientMetadata && !this.clientMetadata.userSystemTimezone) {
            try {
                this.clientMetadata.userSystemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            }
            catch (_) {
                // Ignore exception since the only exception should be due to unavailability of the timezone API in older unsupported browsers
            }
        }
    }
    initSession(sessionInitOptions) {
        sessionInitOptions.onResponse = sessionInitOptions.onResponse || (() => { });
        // Telemetry used to understand token refresh and network reconnect patterns in sessions
        const currentTimeMs = Utils.getCurrentTimeMs();
        const timeSinceLastConnectionClose = this.getSessionStats().lastConnectionClose && (currentTimeMs - this.getSessionStats().lastConnectionClose);
        const timeSinceLastSyncMessage = this.getSessionStats().lastSyncMessage && (currentTimeMs - this.getSessionStats().lastSyncMessage);
        const logOp = new LoggingOperation({
            operationName: 'SessionInit',
        }).start();
        const resultDescription = {
            sessionKey: this.sessionKey,
            sessionId: this.clientMetadata.sessionId,
            timeSinceLastConnectionClose,
            timeSinceLastSyncMessage,
            isTokenRefresh: sessionInitOptions.isTokenRefresh,
            enableRemoteExecutionNotification: this.options.enableRemoteExecutionNotification,
            error: undefined,
            isSeedingRequired: undefined,
        };
        // Initialize the session specifying the protocol, clientMetadata, sessionKey and token
        // SessionKey & origin will be undefined during the first call
        // Token is either auth token provided by the host or anonymous user token generated by the AL service
        const sessionInitMessage = this.options.overrideSessionInitMessage(new SessionInitMessage({
            protocolVersion: currentProtocolVersion,
            clientMetadata: this.clientMetadata,
            sessionKey: this.sessionKey,
            origin: this.origin,
            authToken: this.anonymousToken,
            extensionConfigs: sessionInitOptions.extensionConfigs,
            returnWorkflowInputTypes: true,
            enableRemoteExecutionNotification: this.options.enableRemoteExecutionNotification
        }));
        this.sendMessage(sessionInitMessage, (error, response) => {
            logOp.success = !error;
            logOp.resultSignature = this.getSessionStats().lastConnectionClose ? 'Reconnect' : 'FirstConnect';
            logOp.resourceId = scrubEuiiInUrl(response === null || response === void 0 ? void 0 : response.sliceUrl);
            logOp.setClientMetadata(this.clientMetadata);
            // update sessionKey if different than before
            if ((response === null || response === void 0 ? void 0 : response.sessionKey) && resultDescription.sessionKey !== response.sessionKey) {
                resultDescription.sessionKey = response.sessionKey;
            }
            if (error) {
                resultDescription.error = error.error;
            }
            resultDescription.isSeedingRequired = this.sessionKey !== (response === null || response === void 0 ? void 0 : response.sessionKey);
            logOp.resultDescription = JSON.stringify(resultDescription);
            Log.info(0x1e545703 /* tag_4vf2d */, LogCategory.CoreDefault, logOp.stop());
            sessionInitOptions.onResponse(error, response);
            if (error) {
                this.emit('serverAuthenticationStateChange', ServerAuthenticationState.NotAuthenticated);
                return;
            }
            // If we tried to reconnect on same slice and got signal to reconnect on global url we will do so and avoid further processing of current response.
            if (sessionInitOptions.isReconnectOnSameSlice && response.forceReconnect) {
                this.emit('serverAuthenticationStateChange', ServerAuthenticationState.NotAuthenticated);
                return;
            }
            // We need both anon token and its expiration time to be valid to proceed
            if (!response.anonymousToken || !response.tokenExpirationSeconds) {
                this.emit('serverAuthenticationStateChange', ServerAuthenticationState.NotAuthenticated);
                Log.error(0x1e545702 /* tag_4vf2c */, LogCategory.CoreDefault, 'AL Anonymous token was not generated for the session');
                return;
            }
            this.anonymousToken = response.anonymousToken;
            this.onSuccessfulSessionInitOnServerSide(response);
        });
    }
    onSuccessfulSessionInitOnServerSide(response) {
        var _a, _b;
        this.tokenRefreshManager.scheduleRefresh(TokenType.Anonymous, response.tokenExpirationSeconds, this.initSession.bind(this, { isTokenRefresh: true }), () => {
            this.anonymousToken = undefined;
        });
        this.connectParams = {
            isSeedingRequired: this.sessionKey !== response.sessionKey,
            sessionUrl: `${response.sessionUrlBase}/${response.sessionKey}`,
            origin: response.origin,
            authToken: this.anonymousToken
        };
        if (!((_a = this.options) === null || _a === void 0 ? void 0 : _a.dontSendTokenOnReconnectChangeGate)
            || !response.existingTokenProvisionResponse
            || ((_b = response.existingTokenProvisionResponse) === null || _b === void 0 ? void 0 : _b.tokenType) !== AuthTokenType.AugLoopLowPrivilege) {
            if (this.options.sendTokenFailureMessageChangeGate) {
                this.initHostAuthTokenNew().catch(() => { });
            }
            else {
                this.initHostAuthToken();
            }
        }
        // Determine which events to emit
        const isReconnectAttempt = !!this.sessionKey;
        this.sessionKey = response.sessionKey;
        this.origin = response.origin;
        this.emit('connect', this.connectParams.isSeedingRequired, isReconnectAttempt, this.connectParams.sessionUrl, this.connectParams.origin, this.connectParams.authToken, response.workflowInputTypes, response.downstreamRuntimeWorkflows);
        if (isReconnectAttempt) {
            this.emit('reconnect');
        }
    }
    getAuthToken() {
        const tokenRequest = {
            Tickets: [],
            DocSessionId: this.clientMetadata.docSessionId,
            TokenType: ClientAuthTokenType.Augloop,
            ConnectParams: this.connectParams,
        };
        return this.options.requestAuthToken(tokenRequest)
            .then((tokenResponse) => {
            return tokenResponse;
        })
            .catch(() => {
            return undefined;
        });
    }
    getAuthTokenNew() {
        const tokenRequest = {
            Tickets: [],
            DocSessionId: this.clientMetadata.docSessionId,
            TokenType: ClientAuthTokenType.Augloop,
            ConnectParams: this.connectParams,
        };
        return this.options.requestAuthToken(tokenRequest)
            .then((tokenResponse) => {
            return tokenResponse;
        });
    }
    getAuthTokenTimeoutPromise() {
        const authTokenTimeoutMs = this.options.authTokenTimeoutMs;
        return new Promise((_resolve, reject) => {
            setTimeout(() => {
                reject(new Error(`Host auth token provision took longer than ${authTokenTimeoutMs} ms`));
            }, authTokenTimeoutMs);
        });
    }
    initHostAuthToken() {
        const getAuthToken = this.getAuthToken.bind(this);
        if (this.options.requestAuthToken && getAuthToken) {
            const logOp = new LoggingOperation({ operationName: 'RefreshAuthToken', success: true })
                .setClientMetadata(this.clientMetadata)
                .start();
            getAuthToken()
                .then((tokenResponse) => {
                if (!tokenResponse) {
                    logOp.resultDescription = 'TokenResponse is not set';
                    return;
                }
                if (!tokenResponse.Token) {
                    if (tokenResponse.TokenError == AuthTokenResponseError.TokenMissingInteractionRequired) {
                        this.emit('serverAuthenticationStateChange', ServerAuthenticationState.TokenMissingInteractionRequired);
                        logOp.resultDescription = 'Host auth token provision failed interaction required';
                    }
                    else {
                        this.emit('serverAuthenticationStateChange', ServerAuthenticationState.NotAuthenticated);
                        logOp.resultDescription = 'Host auth token provision failed';
                    }
                    logOp.success = false;
                    Log.info(0x1e545701 /* tag_4vf2b */, LogCategory.CoreDefault, logOp.stop());
                    return;
                }
                Log.info(0x1e545700 /* tag_4vf2a */, LogCategory.CoreDefault, logOp.stop());
                this.sendTokenProvisionMessage(tokenResponse.Token);
            })
                .catch(error => {
                this.emit('serverAuthenticationStateChange', ServerAuthenticationState.NotAuthenticated);
                logOp.success = false;
                logOp.resultDescription = `Error happened while attempting to fetch host token: ${error}`;
                Log.error(0x1e5456e3 /* tag_4vf19 */, LogCategory.CoreDefault, logOp.stop());
            });
        }
    }
    initHostAuthTokenNew() {
        return __awaiter(this, void 0, void 0, function* () {
            const getAuthToken = this.getAuthTokenNew.bind(this);
            if (this.options.requestAuthToken && getAuthToken) {
                const logOp = new LoggingOperation({ operationName: 'RefreshAuthToken', success: true })
                    .setClientMetadata(this.clientMetadata)
                    .start();
                let tokenResponse;
                let errorMessage;
                let authenticationStateOnError = ServerAuthenticationState.NotAuthenticated;
                try {
                    if (this.options.authTokenTimeoutMs > 0) {
                        tokenResponse = yield Promise.race([getAuthToken(), this.getAuthTokenTimeoutPromise()]);
                    }
                    else {
                        tokenResponse = yield getAuthToken();
                    }
                    if (!tokenResponse) {
                        throw new Error('TokenResponse is not set');
                    }
                    if (!tokenResponse.Token) {
                        if (tokenResponse.TokenError == AuthTokenResponseError.TokenMissingInteractionRequired) {
                            authenticationStateOnError = ServerAuthenticationState.TokenMissingInteractionRequired;
                            throw new Error('Host auth token provision failed interaction required');
                        }
                        else {
                            throw new Error('Host auth token provision failed');
                        }
                    }
                }
                catch (error) {
                    errorMessage = error.message;
                    this.emit('serverAuthenticationStateChange', authenticationStateOnError);
                    logOp.success = false;
                    logOp.resultDescription = `Error happened while attempting to fetch host token: ${error}`;
                }
                try {
                    if (errorMessage) {
                        this.sendMessage(new TokenFailureMessage({
                            reason: errorMessage,
                            version: SessionInitializer.initialTokenVersion,
                            clientHandlesResponse: true,
                        }));
                    }
                    else {
                        this.sendTokenProvisionMessage(tokenResponse.Token);
                    }
                }
                catch (error) {
                    if (!errorMessage) {
                        errorMessage = error.message;
                        this.emit('serverAuthenticationStateChange', authenticationStateOnError);
                        logOp.success = false;
                        logOp.resultDescription = `Error happened while attempting to send host token: ${error}`;
                    }
                }
                finally {
                    if (errorMessage) {
                        Log.error(0x1e2a16dd /* tag_4k713 */, LogCategory.CoreDefault, logOp.stop());
                    }
                    else {
                        Log.info(0x1e2a16de /* tag_4k714 */, LogCategory.CoreDefault, logOp.stop());
                    }
                }
            }
        });
    }
    sendTokenProvisionMessage(hostAuthToken) {
        const tokenProvisionMessage = new TokenProvisionMessage({
            authToken: hostAuthToken,
            version: SessionInitializer.initialTokenVersion,
        });
        this.emit('serverAuthenticationStateChange', ServerAuthenticationState.Pending);
        this.sendMessage(tokenProvisionMessage, this.onTokenProvisionResponse.bind(this), true);
    }
    onTokenProvisionResponse(error, response) {
        if (error || !response || !response.tokenExpirationSeconds) {
            this.emit('serverAuthenticationStateChange', ServerAuthenticationState.NotAuthenticated);
            return;
        }
        const authenticatedType = (response.tokenType && response.tokenType == AuthTokenType.WacUserInfo) ? ServerAuthenticationState.WacUserInfoAuthenticated : ServerAuthenticationState.Authenticated;
        this.emit('serverAuthenticationStateChange', authenticatedType);
        // Establish token refresh which will be triggered once token is close to expiry
        this.tokenRefreshManager.scheduleRefresh(TokenType.Host, response.tokenExpirationSeconds, this.initHostAuthToken.bind(this));
    }
}
SessionInitializer.initialTokenVersion = 1;
//# sourceMappingURL=session-initializer.js.map